/** README
 *  封装：系统层级的请求工具
 *        >>> 基于 axios
 *        >>> 对项目提供：封装后的请求类对象
 *
 *  待完善：
 *        1- 将进度条的使用，在实例化时注入
 *            >>> 模块化，不应强依赖某个自定义脚本或插件！
 *
 *        2- 将消息提示框，在实例化时注入
 *            >>> 模块化，不应强依赖某个自定义脚本或插件！
 *            >>> window.dispatchNotify
 *            >>> ElMessageBox
 *            >>> ElLoading
 *
 *        3- 对token的管理，模块化，并在实例化时注入
 *            >>> 模块化，不应强依赖某个自定义脚本或插件！
 *            >>> getToken
 *            >>> formatToken
 *            >>> useUserStoreHook().handRefreshToken
 *
 *
 *
 */


import Axios from "axios";
import { parse, stringify } from "qs";
// import NProgress from "@/utils/progress/index.ts"; // 请求进度条动画
import { getToken, formatToken } from "@/utils/auth";
import useUserStoreHook from "@/store/modules/user";
import { ElMessageBox, ElLoading} from "element-plus";
import { getTimezoneOffset } from "@/utils/timeHelper/index.js";
import { timeHandler } from "@/utils/timeHelper/index.js";
import { canShowRelogin, setReloginShowing } from "@/utils/request/relogin.js";
const $str = window.$str

/** 封装请求类 */
class SystemHttp {

  /** ====== [设置类的私有属性OR方法] ========================================================================================================== */
  static initConfig = {}; // 存储一些初始化的配置，主要用于：实例化时-设置默认的配置、请求拦截or响应拦截时-执行一些回调操作、等

  static isRefreshing = false; // 用于：防止重复刷新token

  static requests = []; // token过期后，暂存待执行的请求
  /** 暂存原始请求 >>> 到【私有类属性requests】中 */
  static stashOriginalRequest(config) {
    return new Promise(resolve => {
      SystemHttp.requests.push((token) => {
        config.headers["Authorization"] = formatToken(token); // 暂存原始请求到【私有类属性requests】中
        // 设置语言
        config.headers['Language'] = window.$str.getLanguage()
        resolve(config);
      });
    });
  }




  /**
   * 构造函数
   * @param {*} baseUrl
   * >>> 设置默认的配置对象
   * >>> 设置请求白名单 >>> 不需要token的接口
   * >>> 管理请求的：错误码信息
   * >>> 创建&初始化Axios实例
   * >>> 设置请求拦截
   * >>> 设置响应拦截
   * >>> 管理消息提示 >>> 后续提取到该模块外部，为方便移植，系统请求模块化，不应受项目使用哪个第三方UI的影响
   */
  constructor(baseUrl) {
    // 1- 设置默认的配置对象 >>> 这里一般是硬编码写死的东西，用于设置默认的固定值
    this.defaultConfig = {
      baseURL: "/bus/",
      timeout: 30000, // 请求超时时间
      headers: { // 默认请求头
        "Accept": "application/json, text/plain, */*",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "From": "Gemini",
        "System-Sign": "gemini3",
      },
      paramsSerializer: { // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
        encode: parse,
        serialize: stringify,
      }
    }

    // 是否使用时区
    if (window.__emind_config?.use_zone) {
      this.defaultConfig.headers["Zone"] = getTimezoneOffset();
    }

    // 2- 设置请求白名单 >>> 不需要token的接口
    this.whiteList = [
      "/ecenter/system/auth/login",
      "/code",
      "/ecenter/system/user/getInfo"
    ];

    // 3- 管理请求的：错误码信息
    this.errorCode = {
      "401": $str.getKey('全局/认证失败，无法访问系统资源'),
      "403": $str.getKey('全局/当前操作没有权限'),
      "404": $str.getKey('全局/访问资源不存在'),
      default: $str.getKey('全局/系统未知错误，请反馈给管理员')
    };

    // 4- 创建&初始化Axios实例
    this.axiosInstance = this.initializeAxios(baseUrl);


    // 5- 设置请求拦截
    this.httpInterceptorsRequest();


    // 6- 设置响应拦截
    this.httpInterceptorsResponse();


    // 7- 管理消息提示 >>> 后续提取到该模块外部，为方便移植，系统请求模块化，不应受项目使用哪个第三方UI的影响
    this.msgBox = null;

  }




  /** ====== [功能方法：封装请求方式] ========================================================================================================== */

  /**
   * 封装：通用请求工具函数
   * >>> 可以单独设置&处理自定义请求/响应回调
   * @param {*} method
   * @param {*} url
   * @param {*} param
   * @param {*} axiosConfig
   * @returns
   */
  request(method, url, param, axiosConfig) {
    const config = {
      method,
      url,
      ...param,
      ...axiosConfig
    };

    return new Promise((resolve, reject) => {
      this.axiosInstance.request(config).then((response) => {
          resolve(response);
      }).catch(error => {
          reject(error);
      });
    });
  }

  /**
   * 封装：GET请求工具函数
   * @param {*} url
   * @param {*} params
   * @param {*} config
   * @returns
   */
  get(url, params, config) {
    return this.request("get", url, { params }, config);
  }


  /**
   * 封装：POST请求工具函数
   * @param {*} url
   * @param {*} params
   * @param {*} config
   * @param {*} urlParams
   * @returns
   */
  post(url, params, config, urlParams) {
    const option = {
      data: params,
      ...(urlParams ? { params: urlParams } : {})
    }
    return this.request("post", url, option, config);
  }


  /**
   * 封装：PUT请求工具函数
   * @param {*} url
   * @param {*} params
   * @param {*} config
   * @returns
   */
  put(url, params, config) {
    return this.request("put", url, { data: params }, config);
  }





  /** ====== [功能方法：辅助] ========================================================================================================== */

  /**
   * 初始化Axios实例
   * @param {*} baseUrl
   * @returns
   */
  initializeAxios(baseUrl) {
    const config = {
      ...this.defaultConfig,
      baseURL: baseUrl ?? this.defaultConfig.baseURL
    }
    return Axios.create(config);
  }


  /** 请求拦截器 */
  httpInterceptorsRequest() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        /** 开启进度条动画 */
        // NProgress.start();

        /** 优先判断post/get等方法是否传入回调
         * 传入了，请求之前执行传入的回调。
         * 未传入，请求前执行SystemHttp类对象默认配置中设置的请求回调。
         */
        if (typeof config.beforeRequestCallback === "function") {
          config.beforeRequestCallback(config);
          return config;
        }
        if (SystemHttp.initConfig.beforeRequestCallback) {
          SystemHttp.initConfig.beforeRequestCallback(config);
          return config;
        }

        /** 白名单检查
         * 放置一些不需要token的接口（通过设置请求白名单，防止token过期后再请求造成的死循环问题）
         */
        if (this.whiteList.some(v => config.url.indexOf(v) > -1)) {
          return config;
        } else {
          return new Promise((resolve) => {
            const token = getToken();
            if (token) {
              const now = new Date().getTime();
              const expired = parseInt(token.expires) - now <= 0; // 判断token是否过期
              if (expired) {
                if (!SystemHttp.isRefreshing) {
                  SystemHttp.isRefreshing = true;
                  // 刷新token
                  useUserStoreHook().handRefreshToken({ refreshToken: token.refreshToken }).then(res => {
                    const token = res?.data?.accessToken ?? '';
                    config.headers["Authorization"] = formatToken(token);
                    // 设置语言
                    config.headers['Language'] = window.$str.getLanguage()
                    SystemHttp.requests.forEach(cb => cb(token)); // token刷新后，重连原始请求
                    SystemHttp.requests = [];
                  })
                  .finally(() => {
                    SystemHttp.isRefreshing = false;
                  });
                }
                resolve(SystemHttp.stashOriginalRequest(config)); // 暂存原始请求到【私有类属性requests】中
              } else {
                config.headers["Authorization"] = formatToken(token);
                config.headers['Language'] = window.$str.getLanguage()
                resolve(config);
              }
            } else {
              resolve(config);
            }
          });
        }
      },
      (error) => {
        return Promise.reject(error);
      },
    );
  }


  /** 响应拦截器 */
  httpInterceptorsResponse() {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        /** 关闭进度条动画 */
        // NProgress.done();

        /** 优先判断post/get等方法是否传入回调
         * 传入了，请求之前执行传入的回调。
         * 未传入，请求前执行SystemHttp类对象默认配置中设置的请求回调。
         */
        if (typeof response.config.beforeResponseCallback === "function") {
          response.config.beforeResponseCallback(response);
          return response.data;
        }
        if (SystemHttp.initConfig.beforeResponseCallback) {
          SystemHttp.initConfig.beforeResponseCallback(response);
          return response.data;
        }

       // 时区处理
        if(window.__emind_config?.use_zone){

          // 需要处理的时间字段
          const timeKeys = ['record_time', 'recordTime'];
          // 处理时间字段
          timeHandler(response.data, timeKeys);
        }



        /** 未设置状态码则默认成功状态 */
        const res = response;
        const code = res.data.code || 200;

        /** 二进制数据则直接返回 */
        if (res.request.responseType === "blob" || res.request.responseType === "arraybuffer") {
          return res.data;
        }

        /** 根据状态码，进行信息提示&返回对应数据 */
        const msg = this.errorCode[code] || res.data.msg || this.errorCode["default"]; // 获取错误信息
        if (code === 401) {
          const isReloginHidden = this.whiteList.some(v => response.config.url.indexOf(v) > -1);
          if (!isReloginHidden) {
            // 判断当前是否已经被重定向到了login页面
            if (canShowRelogin()) {
              setReloginShowing(true);

              this.msgBox = ElMessageBox.confirm(
                $str.getKey('全局/登录状态已过期，您可以继续留在该页面，或者重新登录'),
                $str.getKey('全局/系统提示'),
                {
                  confirmButtonText: $str.getKey('全局/重新登录'),
                  cancelButtonText: $str.getKey('全局/取消'),
                  type: "warning"
                }
              ).then(() => {
                // 用户点击确认后，保持状态锁定，直接跳转（页面跳转后状态会自然重置）
                useUserStoreHook().logOut();
              }).catch(err => {
                // 用户点击取消后，重置状态
                setReloginShowing(false);
              }).finally(() => {
                this.msgBox = null;
                // 注意：如果用户点击确认，状态保持锁定，直到页面跳转
                // 如果用户点击取消，状态已在 catch 中重置
              });
            }

            return Promise.reject(
              new Error($str.getKey('全局/无效的会话，或者会话已过期，请重新登录。'))
            );
          } else {
            return Promise.resolve(res.data);
          }
          // return Promise.reject(
          //   new Error("无效的会话，或者会话已过期，请重新登录。")
          // );
        } else if (code === 500) {
          window.dispatchNotify({
            type: 'error',
            message: msg
          });
          return Promise.reject(new Error(msg));
        } else if (code !== 200) {
          window.dispatchNotify({
            type: 'error',
            message: msg
          });
          return Promise.reject(new Error("error"));
        } else {
          return Promise.resolve(res.data);
        }
      },
      (error) => {
        /** 关闭进度条动画 */
        // NProgress.done();

        /** 根据错误类型-格式化提示消息 */
        error.isCancelRequest = Axios.isCancel(error);
        let { message } = error;
        if (message == "Network Error") {
          message = $str.getKey('全局/网络异常，请检查网络或联系平台管理员');
        } else if (message.includes("timeout")) {
          message = $str.getKey('全局/系统接口请求超时');
        } else if (message.includes("Request failed with status code")) {
          message = $str.getKey('全局/系统接口') + message.substr(message.length - 3) + $str.getKey('全局/异常');
        }
        window.dispatchNotify({
          message: message,
          type: "error",
          duration: 5 * 1000
        });
        return Promise.reject(error);
      }
    );
  }


}



export default SystemHttp;




